/* global __WATCH__:false */

import WebFont from 'webfontloader';
import {
  FONT_BOLD,
  FONT_BOLD_ITALIC,
  FONT_COPY,
  FONT_CSS,
  FONT_EXTRA_BOLD,
  FONT_ITALIC,
  FONT_LIGHT,
  FONT_REGULAR,
  FONT_SUBTITLE,
} from '../constants/AppConstants';

import LoadDisplay from '../ui/loaddisplay/LoadDisplay';
import GameController from '../controller/GameController';
import CutsceneModel from '../model/CutsceneModel';
import { getGameFileLoadList } from 'utils/AssetpackUnloader';
import { addGoogleAnalyticsPlugin } from 'analytics/GoogleAnalytics';
import { DECODE_TIMEOUT_SPEED } from 'app/constants/AppConstants';

export default class extends Phaser.State {
  preload() {
    this.debug = false;

    this.loadComplete = false;

    this.loadBar = new LoadDisplay(this.game);
    this.game.add.existing(this.loadBar);

    this.fontsReady = false;
    this.assetsReady = false;

    this.game.load.onLoadStart.add(this.handleLoadStart, this);
    this.game.load.onFileStart.add(this.handleFileStart, this);

    this.loadFonts();
    this.loadAppAssetPacks();
  }

  handleLoadStart() {
    // console.log('Load.handleLoadStart');
    getGameFileLoadList(this.game, this.debug);
  }

  handleFileStart(progress, key, url) {
    progress;
    key;
    url;

    // store audioatlse names to add them to sound manager
    if (key.indexOf('-audioatlas') !== -1) {
      this.audioSprites.push(key.replace('-audioatlas', ''));
    }
  }

  loadFonts() {
    WebFont.load({
      custom: {
        families: [
          FONT_COPY,
          FONT_SUBTITLE,
          FONT_REGULAR,
          FONT_ITALIC,
          FONT_EXTRA_BOLD,
          FONT_BOLD,
          FONT_BOLD_ITALIC,
          FONT_LIGHT,
        ],
        urls: [this.game.urls.getFontsDirectory(FONT_CSS)],
      },
      active: this.fontsLoaded.bind(this),
      inactive: this.fontsLoaded.bind(this),
    });
  }

  loadAppAssetPacks() {
    this.game.load.baseURL = this.game.urls.getAssetsDirectory();
    this.game.load.crossOrigin = 'anonymous';

    const loadingData = this.game.cache.getJSON('loading_data').main;

    this.audioSprites = [];

    // load assetpacks
    const packs = loadingData.packs;
    for (let i = 0; i < packs.length; i++) {
      const pack = packs[i];
      this.game.load.pack(pack.key, pack.path, null, this);
    }

    // load characters
    const chars = loadingData.characters;
    for (let i = 0; i < chars.length; i++) {
      const charId = chars[i];
      this.game.load.pack('load', this.game.urls.getCharacterPath(charId) + 'assetpack.json');
    }

    // load cutscenes
    const cutsceneModel = new CutsceneModel(this.game.cache.getJSON('data_cutscenes'));
    const cutscenes = loadingData.cutscenes;
    for (let i = 0; i < cutscenes.length; i++) {
      const cutsceneId = cutscenes[i];
      const data = cutsceneModel.getScene(cutsceneId);
      this.loadCutsceneAssets(cutsceneId, data);
    }
  }

  loadCutsceneAssets(cutsceneId, data) {
    let path = this.game.urls.getCutscenePath(cutsceneId);

    // load spine files
    for (let i = 0; i < data.skeletons.length; i++) {
      const id = data.skeletons[i];
      const key = id;
      this.game.load.spine(key, path + 'spine/' + id + '.json');
    }

    if (!data.onlyAnim) {
      // load captions
      this.game.load.json(cutsceneId + '_captions', path + 'captions.json');

      // load audio
      path += 'audio/64/' + cutsceneId;
      this.game.load.audioSprite(
        cutsceneId + '_vo',
        [path + '.m4a', path + '.mp3'],
        path + '.json'
      );
    }
  }

  loadUpdate() {
    this.loadBar.visible = true;
    this.loadBar.setProgress(this.game.load.progress / 100);
  }

  create() {
    this.assetsReady = true;
    this.game.sounds.startup();
  }

  fontsLoaded() {
    this.fontsReady = true;
    this.checkLoading();
  }

  update() {
    this.checkLoading();
  }

  checkLoading() {
    if (!this.loadComplete && this.assetsReady && this.fontsReady) {
      this.loadBar.setProgress(1);
      this.handleLoadComplete();
    }
  }

  handleLoadComplete() {
    if (!this.loadComplete) {
      this.loadComplete = true;
      this.game.app_config = this.game.cache.getJSON('app_config');
      this.setControllers();

      // register audio sprites
      for (let i = 0; i < this.audioSprites.length; i++) {
        this.game.sounds.addAudioSprite({ id: this.audioSprites[i] });
      }

      //
      // TODO : auto register audio sprites
      for (let i = 0; i < this.game.app_config.audio.length; i++) {
        this.game.sounds.addSound(this.game.app_config.audio[i], this.game.app_config.audio[i]);
      }

      // if (doesRequireLightVersion() === false) {
      //   for (const id of this.game.app_config.audioSpritesExtra) {
      //     // console.log('ADDING AUDIO SPRITE : ' + id);
      //     this.game.sounds.addAudioSprite({ id });
      //   }
      // }
      this.game.sounds.decode(this.audioSprites, this.audioDecoded, this);
      this.decodeSoundsCallback = this.game.time.events.add(DECODE_TIMEOUT_SPEED, () => {
        this.decodeSoundsCallback = null;
        if (this.loadBar) {
          this.loadBar.setProcessing();
        }
      });
    }
  }

  audioDecoded() {
    if (this.decodeSoundsCallback) {
      this.game.time.events.remove(this.decodeSoundsCallback);
      this.decodeSoundsCallback = null;
    }
    if (__WATCH__) {
      // skip the load complete animation during dev :)
      this.handleLoadAnimationComplete();
    } else {
      this.loadBar.onAnimationComplete.add(this.handleLoadAnimationComplete, this);
      this.loadBar.animateCompleteSequence();
    }

    // Add Springroll google analytics plugin to google analytics bridge
    const plugin = this.game.springRollApplication.getPlugin('GoogleAnalytics');
    addGoogleAnalyticsPlugin({ plugin });
  }

  setControllers() {
    this.game.controller = new GameController(this.game);
    this.game.controller.init(this.game.cache.getJSON('gamedata'));
  }

  handleLoadAnimationComplete() {
    this.game.controller.showWelcomeScreen();
    this.loadComplete = true;
  }

  shutdown() {
    this.game.load.onLoadStart.remove(this.handleLoadStart, this);
    this.game.load.onFileStart.remove(this.handleFileStart, this);
    this.loadBar.onAnimationComplete.remove(this.handleLoadAnimationComplete, this);
    this.loadBar.destroy();
    this.loadBar = null;
  }
}
